import { Helmet } from 'react-helmet-async'

interface JsonLdProps {
  data: object | object[]
}

export default function JsonLd({ data }: JsonLdProps) {
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(Array.isArray(data) ? data : [data])}
      </script>
    </Helmet>
  )
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://celebritypicks.app'

export function personSchema(celebrity: {
  name: string
  slug: string
  bio?: string | null
  imageUrl?: string | null
  birthYear?: number | null
  category: string
  wikipediaUrl?: string | null
  twitterHandle?: string | null
  websiteUrl?: string | null
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: celebrity.name,
    url: `${SITE_URL}/celebrities/${celebrity.slug}`,
    ...(celebrity.bio && { description: celebrity.bio }),
    ...(celebrity.imageUrl && { image: celebrity.imageUrl }),
    ...(celebrity.birthYear && { birthDate: String(celebrity.birthYear) }),
    jobTitle: celebrity.category,
    sameAs: [
      celebrity.wikipediaUrl,
      celebrity.twitterHandle ? `https://twitter.com/${celebrity.twitterHandle}` : null,
      celebrity.websiteUrl,
    ].filter(Boolean),
  }
}

export function mediaSchema(media: {
  title: string
  slug: string
  type: 'movie' | 'tv_show'
  year?: number | null
  description?: string | null
  posterUrl?: string | null
  rating?: number | null
  genres?: { name: string }[] | null
  imdbId?: string | null
}) {
  return {
    '@context': 'https://schema.org',
    '@type': media.type === 'movie' ? 'Movie' : 'TVSeries',
    name: media.title,
    url: `${SITE_URL}/media/${media.slug}`,
    ...(media.year && { dateCreated: String(media.year) }),
    ...(media.description && { description: media.description }),
    ...(media.posterUrl && { image: media.posterUrl }),
    ...(media.rating && { aggregateRating: { '@type': 'AggregateRating', ratingValue: media.rating, bestRating: 10 } }),
    ...(media.genres?.length && { genre: media.genres.map((g) => g.name) }),
    ...(media.imdbId && { sameAs: `https://www.imdb.com/title/${media.imdbId}` }),
  }
}

export function itemListSchema(items: { name: string; url: string; position: number }[], listName: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listName,
    itemListElement: items.map((item) => ({
      '@type': 'ListItem',
      position: item.position,
      name: item.name,
      url: item.url,
    })),
  }
}

export function breadcrumbSchema(crumbs: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url.startsWith('http') ? crumb.url : `${SITE_URL}${crumb.url}`,
    })),
  }
}

export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Celebrity Picks',
    url: SITE_URL,
    description: 'Source-verified movie and TV recommendations from celebrities and public figures.',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  }
}
